import * as React from 'react';

import {
  chatApiBase,
  chatFetch,
  getChatHeaders,
} from '@modules/_shared/components/chat/chatFetch';
import {
  type ModelInfo,
  resolveDefaultModelId,
} from '@modules/_shared/components/chat/modelSelection';

export type { ModelInfo };

export const useModels = (
  persistedModelRef?: React.MutableRefObject<string | null>,
) => {
  const [models, setModels] = React.useState<ModelInfo[]>([]);
  const [modelsLoading, setModelsLoading] = React.useState(true);
  const [modelsError, setModelsError] = React.useState<string | null>(null);
  const selectedModelRef = React.useRef<string | null>(
    persistedModelRef?.current ?? null,
  );
  const defaultModelIdRef = React.useRef<string | null>(null);
  const modelsRef = React.useRef<ModelInfo[]>([]);
  // New-chat clicked before GET /models resolved: don't apply null (that would
  // keep the previous chat's model once the catalog lands, or flash
  // "Select model"). Honor this on the fetch instead.
  const pendingDefaultRef = React.useRef(false);
  const [selectedModelId, setSelectedModelId] = React.useState<string | null>(
    persistedModelRef?.current ?? null,
  );

  const applyModelId = React.useCallback(
    (modelId: string | null) => {
      setSelectedModelId(modelId);
      selectedModelRef.current = modelId;
      if (persistedModelRef) {
        persistedModelRef.current = modelId;
      }
    },
    [persistedModelRef],
  );

  React.useEffect(() => {
    const controller = new AbortController();
    setModelsLoading(true);
    setModelsError(null);
    chatFetch(`${chatApiBase}/models`, {
      headers: getChatHeaders(),
      signal: controller.signal,
    })
      .then(async res => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(
            data?.error ?? `Failed to load chat models (${res.status})`,
          );
        }
        return data;
      })
      .then(data => {
        if (controller.signal.aborted) {
          return;
        }
        const fetchedModels: ModelInfo[] = data.models ?? [];
        modelsRef.current = fetchedModels;
        setModels(fetchedModels);
        if (fetchedModels.length === 0) {
          setModelsError('No AI models configured on this environment.');
          pendingDefaultRef.current = false;
          return;
        }
        const defaultId = resolveDefaultModelId(
          fetchedModels,
          data.defaultModelId,
        );
        defaultModelIdRef.current = defaultId;
        const currentModelId = selectedModelRef.current;
        if (
          pendingDefaultRef.current ||
          !currentModelId ||
          !fetchedModels.some(model => model.id === currentModelId)
        ) {
          applyModelId(defaultId);
        }
        pendingDefaultRef.current = false;
      })
      .catch(err => {
        if (controller.signal.aborted) {
          return;
        }
        setModels([]);
        modelsRef.current = [];
        pendingDefaultRef.current = false;
        setModelsError(
          err instanceof Error ? err.message : 'Failed to load chat models',
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setModelsLoading(false);
        }
      });
    return () => controller.abort();
  }, [applyModelId]);

  const handleModelChange = React.useCallback(
    (modelId: string) => {
      applyModelId(modelId);
    },
    [applyModelId],
  );

  const selectLoadedModel = React.useCallback(
    (modelId: string) => {
      // A loaded chat's own model wins over a queued "reset to default" from a
      // New chat / legacy-chat click that landed while /models was in flight —
      // otherwise the models fetch would clobber this selection on resolve.
      pendingDefaultRef.current = false;
      const currentModels = modelsRef.current;
      if (
        currentModels.length === 0 ||
        currentModels.some(model => model.id === modelId)
      ) {
        applyModelId(modelId);
        return;
      }
      const defaultId = defaultModelIdRef.current;
      if (defaultId) {
        applyModelId(defaultId);
      }
    },
    [applyModelId],
  );

  const resetToDefaultModel = React.useCallback(() => {
    const defaultId = defaultModelIdRef.current;
    if (defaultId) {
      applyModelId(defaultId);
      pendingDefaultRef.current = false;
      return;
    }
    pendingDefaultRef.current = true;
  }, [applyModelId]);

  return {
    models,
    selectedModelId,
    selectedModelRef,
    handleModelChange,
    selectLoadedModel,
    resetToDefaultModel,
    modelsLoading,
    modelsError,
  };
};
